import { Alert, Box, ButtonLink, Heading, Pill, Stack, Text } from '@sixfold/common-ui';
import { isNil, notNil } from '@sixfold/typed-primitives';
import React from 'react';

import { FormattedDate } from '../../components/date_formatting/formatted_date';
import { CompanyAgreementQuery } from '../../lib/graphql';
import { DPA_MINIMUM_PUA_VERSION } from '../utils';

type CompanyAgreement = NonNullable<NonNullable<CompanyAgreementQuery['company']>['agreement']>;

export interface CompanyAgreementViewProps {
  agreement: CompanyAgreement | undefined;
}

interface DpaStatus {
  kind: React.ComponentProps<typeof Pill>['kind'];
  label: string;
}

function dpaStatus(agreement: CompanyAgreement): DpaStatus {
  if (agreement.agreementVersion === null) {
    return { kind: 'neutral', label: 'Agreement present (PUA version unknown)' };
  }
  if (agreement.agreementVersion >= DPA_MINIMUM_PUA_VERSION) {
    return { kind: 'success', label: `DPA included (PUA v${agreement.agreementVersion})` };
  }
  return { kind: 'warning', label: `No DPA (PUA v${agreement.agreementVersion}, pre-v${DPA_MINIMUM_PUA_VERSION})` };
}

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Stack direction="row" gap="medium" alignItems="center">
    <Box style={{ minWidth: '160px' }}>
      <Text color="subtle">{label}</Text>
    </Box>
    {children}
  </Stack>
);

const AgreementDetails: React.FC<{ agreement: CompanyAgreement }> = ({ agreement }) => {
  const status = dpaStatus(agreement);

  return (
    <Stack direction="column" gap="xsmall">
      <DetailRow label="DPA status">
        <Pill kind={status.kind}>{status.label}</Pill>
      </DetailRow>
      <DetailRow label="PUA version">
        <Text>{agreement.agreementVersion !== null ? `v${agreement.agreementVersion}` : '(unknown)'}</Text>
      </DetailRow>
      <DetailRow label="Contractual partner">
        <Text>{agreement.contractualPartner ?? '(unknown)'}</Text>
      </DetailRow>
      <DetailRow label="Signed date">
        <Text>
          {agreement.signedDate !== null ? (
            <FormattedDate date={agreement.signedDate} showTime={false} showTimezone={false} />
          ) : (
            '(unknown)'
          )}
        </Text>
      </DetailRow>
      <DetailRow label="Last updated">
        <Text>
          <FormattedDate date={agreement.updatedAt} />
        </Text>
      </DetailRow>
    </Stack>
  );
};

export const CompanyAgreementView: React.FC<CompanyAgreementViewProps> = ({ agreement }) => {
  return (
    <Stack direction="column" gap="medium">
      <Stack direction="column" gap="small">
        <Stack direction="row" gap="medium" alignItems="center">
          <Heading size="large">Carrier DPA / PUA contract status</Heading>
          <ButtonLink
            kind="subtle"
            size="small"
            href="https://support.transporeon.com/servicedesk/customer/portal/3/PRODUCTMGT-9291"
            target="_blank"
            rel="noopener noreferrer">
            Learn more
          </ButtonLink>
        </Stack>
        <Text>
          Shows whether this carrier has signed a Platform User Agreement (PUA) and which version. The Data Processing
          Addendum (DPA) is part of <strong>PUA v{DPA_MINIMUM_PUA_VERSION}</strong> and later, which is required to
          enable certain Visibility features (e.g. Historical Asset Tracking).
        </Text>
      </Stack>

      {isNil(agreement) || !agreement.hasAgreement ? (
        <Stack direction="column" gap="small">
          <Alert kind="warning">
            This carrier is on a <strong>Light Registration</strong> (uses functionality free of charge) and has no
            Platform User Agreement. It is not in scope for features that require a DPA / PUA{' '}
            {`v${DPA_MINIMUM_PUA_VERSION}+`} contract.
          </Alert>
          {notNil(agreement) && (
            <DetailRow label="Last updated">
              <Text>
                <FormattedDate date={agreement.updatedAt} />
              </Text>
            </DetailRow>
          )}
        </Stack>
      ) : (
        <AgreementDetails agreement={agreement} />
      )}
    </Stack>
  );
};
